import React, { useState } from 'react';
import { Input, Button, Modal, message, Spin } from 'antd';
import { useProductGradeForm } from './hooks/useProductGradeForm';

const ProductGradeForm: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, description, setDescription,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, doCopy, quickSearch,
  } = useProductGradeForm();

  const [searchCode, setSearchCode] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyCode, setCopyCode] = useState('');

  // Delete with confirmation
  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除等級 ${current.f118_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  // Copy with code input
  const handleCopyOpen = () => {
    if (!current) return;
    setCopyCode('');
    setCopyModalOpen(true);
  };

  const handleCopyConfirm = async () => {
    if (!copyCode.trim()) {
      message.warning('請輸入新代號');
      return;
    }
    await doCopy(copyCode.trim());
    setCopyModalOpen(false);
  };

  // Quick search
  const handleQuickSearch = () => {
    if (searchCode.trim()) {
      quickSearch(searchCode.trim());
    }
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-2xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          產品等級定義 (pw1118f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入代號快速查詢"
            value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 180 }}
            maxLength={2}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">等級代號 (F118_01)</label>
            <Input
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={2}
              disabled={mode !== 'add'}
              style={{ width: 120 }}
              placeholder="最多2碼"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">等級說明 (F118_11)</label>
            <Input
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={30}
              disabled={!isEditing}
              style={{ width: 400 }}
              placeholder="最多30字"
            />
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f118_91} | 建立日期: {current.f118_92 || '-'}</div>
            <div>修改人員: {current.f118_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
              <Button onClick={handleCopyOpen} disabled={!current}>複製</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>

        {/* Copy Modal */}
        <Modal
          title="複製等級"
          open={copyModalOpen}
          onOk={handleCopyConfirm}
          onCancel={() => setCopyModalOpen(false)}
          okText="確定"
          cancelText="取消"
        >
          <div className="py-2">
            <label className="block text-sm mb-1">新等級代號：</label>
            <Input
              value={copyCode}
              onChange={e => setCopyCode(e.target.value)}
              maxLength={2}
              style={{ width: 120 }}
              autoFocus
            />
          </div>
        </Modal>
      </div>
    </Spin>
  );
};

export default ProductGradeForm;
